import {Component, EventEmitter, Input, Output, OnDestroy, HostListener, inject} from '@angular/core';
import {LinkComponent} from "../link/link.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {DatePipe} from "@angular/common";
import {ContentComponent} from "../content/content.component";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {AvatarComponent} from "../avatar/avatar.component";
import {PillComponent} from "../pill/pill.component";
import {RouterService} from "../../services/router.service";
import {interval, Subscription} from "rxjs";
import {NotificationResource} from '../../types/notifications-types/notificationResource';
import {ModuleConfigurationService} from '../../services/module-configuration.service';

@Component({
    selector: 'app-notification',
    imports: [
        LinkComponent,
        TranslatePipe,
        ContentComponent,
        DatePipe,
        IconBadgeComponent,
        AvatarComponent,
        PillComponent
    ],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.scss'
})
export class NotificationComponent implements OnDestroy {
  private routerService: RouterService = inject(RouterService);
  private modConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);

  /** Accept a single notification (this is for app-bar) test*/
  @Input() public set notification(v: NotificationResource | null | undefined) {
    this._source = v ? [v] : [];
    this.reGenerate();
  }
  /** Accept an array (this is for home to rotate the notifications) */
  @Input() public set notifications(v: NotificationResource[] | null | undefined) {
    this._source = Array.isArray(v) ? v : [];
    this.reGenerate();
  }

  /** Auto-advance every N ms (only if there's >1 item) */
  @Input() public intervalMs: number = 5000;
  /**first N notification */
  @Input() public topN: number = 5;

  /**Separate the same component out for cases when it is used in the page or in the notification list */
  @Input() public variant: 'fixed' | 'compact' = 'fixed';

  /**Filter out the onse which are marked important */
  @Input() public favorImportant: boolean = true;
  @Output() public asRead: EventEmitter<number> = new EventEmitter<number>();
  @Output() public asUnread: EventEmitter<number> = new EventEmitter<number>();
  /** Oringal source */
  private _source: NotificationResource[] = [];
  /** Current index into notifications */
  private _notifications: NotificationResource[] = [];
  public index: number = 0;

  private rotateSub?: Subscription;
  private paused: boolean = false;

  private subscriptions: Subscription[] = [];

  //TODO a save method is required in this component which will save the read notifications but this component also needs a better service which manages the marked read notifications in here for both event when somebody is marking in the bar-top or in the home page the read notifications!


  /** Current notification (or null) */
  public get current(): NotificationResource | null {
    return this._notifications.length ? this._notifications[this.index] : null;
  }

  /** Navigation */
  public next(): void {
    if (!this._notifications.length) return;
    this.index = (this.index + 1) % this._notifications.length;
  }
  public prev(): void {
    if (!this._notifications.length) return;
    this.index = (this.index - 1 + this._notifications.length) % this._notifications.length;
  }

  /*Filter method */
  private reGenerate(): void {
    const base = this._source;

    //favor important but keep original order within each group
    let prioritized = base;
    if (this.favorImportant) {
      const imp = base.filter(n => n.important);
      const rest = base.filter(n => !n.important);
      prioritized = [...imp, ...rest];
    }

    //cap to topN
    const next = prioritized.slice(0, this.topN);

    //adjust index if needed
    const prevLen = this._notifications.length;
    this._notifications = next;
    if (this.index >= this._notifications.length) {
      this.index = this._notifications.length ? 0 : 0;
    }

    //restart ticker only when it is the case
    if (prevLen !== this._notifications.length) {
      this.ensureTicker();
    }
  }

  /** Auto-rotate ticker */
  private ensureTicker(): void {
    this.rotateSub?.unsubscribe();
    if (this._notifications.length > 1 && this.intervalMs > 0) {
      this.rotateSub = interval(this.intervalMs).subscribe(() => {
        if (!this.paused) this.next();
      });
    }
  }

  @HostListener('mouseenter') public onEnter(): void { this.paused = true; }
  @HostListener('mouseleave') public onLeave(): void { this.paused = false; }

  public setRead(event: Event, menuTrigger: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.asRead.emit(this.current!.id);
    this.closeDropdown(menuTrigger);
  }
  
  public setUnRead(event: Event, menuTrigger: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.asUnread.emit(this.current!.id);
    this.closeDropdown(menuTrigger);
  }

  private closeDropdown(menuTrigger: HTMLElement): void {
    menuTrigger.setAttribute('aria-expanded', 'false');
    const dropdown = menuTrigger.closest('.dropdown');
    dropdown?.classList.remove('show');
    const menu = dropdown?.querySelector('.dropdown-menu');
    menu?.classList.remove('show');
  }

  public open(): void {
    const n = this.current;
    if (!n) return;
    if(!this.current.read) {
      this.asRead.emit(this.current!.id);
    }
    if(n.model !== null) {
      const route = this.modConfigService.getRouteForGenericModelResource(
        n.model.type, 
        String(n.model.id),
        String(n.model.parent_id)
      );
      if(route !== null) {
        this.routerService.navigateTo(route);
      }
    }
  }

  public stopPrevent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public ngOnDestroy(): void {
    this.rotateSub?.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public createDate(date: string): Date {
    return new Date(date);
  }
}
